import { WorkspaceCategoryEnum } from '@sr-sdks/api-sdk-axios';
import {
  Combobox,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Pill,
  useCombobox,
} from '@sr/ui-library';
import { Control } from 'react-hook-form';
import * as yup from 'yup';
import { IfFeatureEnabledMSR3 } from '../../../../components/IfFeatureEnabledMSR3';
import { toTitleCase } from '../../../../utils/helpers/common';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import type { WorkspaceDetailsFormValues } from '../../shared/types';

interface CategoriesFieldProps {
  control: Control<WorkspaceDetailsFormValues>;
}

const CategoriesCombobox = ({
  field,
}: {
  field: {
    onChange: (categories: WorkspaceCategoryEnum[]) => void;
    value: WorkspaceCategoryEnum[] | undefined;
  };
}) => {
  const combobox = useCombobox({
    options: Object.values(WorkspaceCategoryEnum).map((category) => ({
      label: toTitleCase(category),
      value: category,
    })),
    selected: field.value ?? [],
    setSelected: field.onChange as (categories: string[]) => void,
  });

  return (
    <FormItem>
      <FormLabel>Workspace Category</FormLabel>
      <FormControl>
        <Combobox classes={{ dropdownTrigger: 'mb-3' }} store={combobox} />
      </FormControl>
      <div className="flex flex-wrap gap-2">
        {combobox.selected.map((category) => (
          <Pill
            key={category}
            size="xs"
            onClick={() => combobox.handleSelect(category)}
          >
            {toTitleCase(category)}
          </Pill>
        ))}
      </div>
      <FormMessage />
    </FormItem>
  );
};

export const CategoriesField = ({ control }: CategoriesFieldProps) => (
  <IfFeatureEnabledMSR3 name="workspace.settings.details.category">
    <FormField
      name={WORKSPACE_DETAILS_FORM_FIELDS.categories}
      control={control}
      render={({ field }) => <CategoriesCombobox field={field} />}
    />
  </IfFeatureEnabledMSR3>
);

CategoriesField.schema = yup
  .array()
  .of(yup.string<WorkspaceCategoryEnum>())
  .nullable();
